import { useEffect, useState } from "react";

import { MailIcon } from "@/assets/icons";
import type { StatusFilterType } from "@/components/admin/invitations";
import { InvitationsFilter, InvitationsList } from "@/components/admin/invitations";
import { strings } from "@/constants";
import { AlertVariantEnum } from "@/enums";
import {
    useCancelInvitation,
    useConfirm,
    useListInvitations,
    useToast,
} from "@/hooks";

export const AdminInvitationsPage = () => {
    const [statusFilter, setStatusFilter] = useState<StatusFilterType>("all");

    const { onShow: showToastHandler } = useToast();

    const { onAsk: askConfirmHandler } = useConfirm();

    const {
        data,
        isLoading,
        onRun: fetchInvitationsHandler,
    } = useListInvitations();

    const {
        isLoading: isCancelling,
        onRun: runCancelHandler,
    } = useCancelInvitation();

    const filteredInvitations = (data ?? []).filter(({ status }) => statusFilter === "all" || status === statusFilter);

    const cancelHandler = async (invitationId: number) => {
        const confirmed = await askConfirmHandler({
            confirmLabel: strings.admin.cancelInvitation,
            isDanger: true,
            message: strings.admin.confirmCancelInvitation,
            title: strings.admin.cancelInviteTitle,
        });

        if (!confirmed) return;

        try {
            await runCancelHandler(invitationId);

            await fetchInvitationsHandler({});

            showToastHandler({
                message: strings.admin.cancelInviteSuccess,
                variant: AlertVariantEnum.SUCCESS,
            });
        } catch (error) {
            showToastHandler({
                message: error instanceof Error ? error.message : strings.errors.tryAgain,
                variant: AlertVariantEnum.ERROR,
            });
        }
    };

    useEffect(
        () => void fetchInvitationsHandler({}),
        [fetchInvitationsHandler],
    );

    return (
        <div
            className="
                mx-auto
                w-full
                max-w-5xl
                space-y-6
            "
        >
            <div className="flex items-center gap-2 sm:gap-3">
                <MailIcon className="h-6 w-6 shrink-0 text-primary sm:h-7 sm:w-7" />
                <h1 className="text-xl font-bold text-text-primary sm:text-2xl">{strings.admin.invitationsTitle}</h1>
            </div>
            <InvitationsFilter
                value={statusFilter}
                onChange={setStatusFilter}
            />
            <InvitationsList
                invitations={filteredInvitations}
                isCancelling={isCancelling}
                isLoading={isLoading}
                onCancel={cancelHandler}
            />
        </div>
    );
};
